import { useEffect, useState } from 'react'

/**
 * Custom hook that provides a debounced version of a value.
 *
 * This hook is useful for delaying an update to a value.
 * For example, you might use it to postpone a search query
 * until after the user has stopped typing for a certain
 * amount of time.
 *
 * @template T - The type of the value to be debounced.
 * @param {T} value - The input value that should be debounced.
 * @param {number} [ms=0] - The debounce delay in milliseconds.
 * Defaults to 0 if not provided.
 * @returns {T} - The debounced value that will only update
 * after the specified delay.
 */
export const useDebounce = <T>(value: T, ms = 0) => {
    const [debounce, setDebounce] = useState<T>(value)
    useEffect(() => {
        const timeout = setTimeout(() => setDebounce(value), ms)
        return () => clearTimeout(timeout)
    }, [value, ms])

    return debounce
}
